import { ISaSmsFeeItem } from '@interfaces/sa-sms';
import { convertPrice } from '@utils/resource';
import { Button, Space, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

type Props = {
    items: ISaSmsFeeItem[];
    canEdit: boolean;
    onEdit: (item: ISaSmsFeeItem) => void;
    onVerify: (item: ISaSmsFeeItem) => void;
};

/**
 * Twilio A2P fees. Each row is an application plan (D-1018-1): brand registration and campaign vetting are `one_time` plans of the
 * SMS A2P Registration app, the monthly campaign fee is the SMS A2P Campaign plan. Shows the label stored on the plan.
 */
export const SmsFeeItemsTable: React.FC<Props> = ({ items, canEdit, onEdit, onVerify }) => {
    const { t } = useTranslation(['sa-sms']);
    return (
        <Table<ISaSmsFeeItem> rowKey="code" size="small" pagination={false} dataSource={items}>
            <Table.Column<ISaSmsFeeItem> title={t('settings.feeItem')} render={(_, f) => f.label} />
            <Table.Column<ISaSmsFeeItem>
                title={t('settings.cadenceTitle')}
                render={(_, f) => t(`settings.cadence.${f.feeType}`, { defaultValue: f.feeType })}
            />
            <Table.Column<ISaSmsFeeItem>
                title={t('settings.price')}
                align="right"
                render={(_, f) => convertPrice(f.amount)}
            />
            <Table.Column<ISaSmsFeeItem>
                title={t('settings.active')}
                render={(_, f) =>
                    // The monthly campaign fee is a live subscription price: it has no on/off switch (amount 0 waives it).
                    f.feeType === 'monthly' ? (
                        '-'
                    ) : (
                        <Tag color={f.active ? 'success' : 'default'}>
                            {f.active ? t('common.on') : t('common.off')}
                        </Tag>
                    )
                }
            />
            <Table.Column<ISaSmsFeeItem>
                title={t('settings.verifiedAt')}
                render={(_, f) =>
                    f.verifiedAt ?? <Tag color="warning">{t('settings.unverified')}</Tag>
                }
            />
            <Table.Column<ISaSmsFeeItem>
                title=""
                render={(_, f) =>
                    canEdit && (
                        <Space>
                            <Button size="small" onClick={() => onEdit(f)}>
                                {t('actions.edit')}
                            </Button>
                            <Button size="small" onClick={() => onVerify(f)}>
                                {t('settings.markVerified')}
                            </Button>
                        </Space>
                    )
                }
            />
        </Table>
    );
};
